import React, { lazy, Suspense, useEffect, useState } from "react";
import "./App.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import InstallPWAButton from "./InstallPWAButton";
import { useNavigate } from "react-router-dom";
import ShootingStarsCursor from "./SubtleTrailingCursorGlow";

const HomePage = lazy(() => import("./components/HomePage"));
const SignInForm = lazy(() => import("./components/SignInForm"));
const SignUpForm = lazy(() => import("./components/SignUpForm"));

const App = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [triggerReload, setTriggerReload] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
  const messaging = getMessaging();
  const navigate = useNavigate();

  // Fetch user info + permissions on reload trigger
  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    const storedUsername = localStorage.getItem("username");
    const storedEmail = localStorage.getItem("email");
    if (storedUserId) {
      setIsLoggedIn(true);
      setUserId(storedUserId);
      setUsername(storedUsername);
      setEmail(storedEmail);
      fetchPermissions(storedUserId);
    } else {
      setIsLoading(false);
    }
  }, [triggerReload]);

  const fetchPermissions = async (userId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/users/${userId}/permissions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      setPermissions(data.permissions?.filter((p) => p !== null) || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check and update FCM token on app start or login
  const checkAndUpdateToken = async (userId) => {
    try {
      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY,
      });
      const savedToken = localStorage.getItem("fcmToken");
      if (currentToken && currentToken !== savedToken) {
        localStorage.setItem("fcmToken", currentToken);
        await sendTokenToServer(currentToken, userId);
        console.log("FCM token updated and sent to server");
      } else {
        console.log("FCM token unchanged or not available");
      }
    } catch (err) {
      console.error("Failed to get FCM token:", err);
    }
  };

  // On login, set user info and check FCM token
  const handleLogin = async (userId, username, email, token, permissions) => {
    setIsLoggedIn(true);
    setUserId(userId);
    setUsername(username);
    setEmail(email);
    setPermissions(permissions);
    localStorage.setItem("userId", userId);
    localStorage.setItem("username", username);
    localStorage.setItem("email", email);
    localStorage.setItem("token", token);
    setTriggerReload((prev) => !prev);

    await checkAndUpdateToken(userId);
  };

  const sendTokenToServer = async (token, userId) => {
    if (!userId) {
      console.error("User ID not found when sending token");
      return;
    }
    try {
      await fetch(`${API_URL}/api/save-fcm-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, userId }),
      });
    } catch (err) {
      console.error("Error sending token to server:", err);
    }
  };

  // Listen for foreground messages (optional)
  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("Message received. ", payload);
      // You can display a toast or notification here
    });
    return () => unsubscribe();
  }, [messaging]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "gmail-auth-success") {
        if (event.data.success) {
          alert(`Gmail access successfully configured for ${event.data.email}`);
        } else {
          alert(`Failed to configure Gmail: ${event.data.error}`);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  if (isLoading) return <div>Loading...</div>;

  function isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Suspense fallback={<div>Loading...</div>}>
        {isLoggedIn ? (
          // Wrapped HomePage and ShootingStarsCursor in a React Fragment
          <>
            <HomePage
              isLoggedIn={isLoggedIn}
              userId={userId}
              username={username}
              email={email}
              setIsLoggedIn={setIsLoggedIn}
              permissions={permissions}
              setTriggerReload={setTriggerReload}
              isIOS={isIOS}
              handleEnableNotifications={() => {}}
            />
            {/* <ShootingStarsCursor /> */}
          </>
        ) : isSignUp ? (
          <SignUpForm onSignInClick={() => setIsSignUp(false)} />
        ) : (
          <>
            <SignInForm
              onSignUpClick={() => setIsSignUp(true)}
              onLogin={handleLogin}
            />
            {isIOS() && <InstallPWAButton />}
          </>
        )}
      </Suspense>
    </GoogleOAuthProvider>
  );
};

export default App;
